import React, { memo, useEffect, useRef } from "react";

/**
 * The scan's tile grid and its kitt bar, drawn on canvas.
 *
 * These were an SVG chart declared inside Scan's own body, which gave it a new
 * component identity on every render: React could not diff it, so each of the
 * hundreds of progress messages a /24 produces tore down ~1200 SVG nodes and
 * built them again, and every pending tile carried its own SMIL <animate>.
 *
 * Nothing here re-renders. The component takes one ref - `source` - that Scan
 * keeps current, reads it inside a requestAnimationFrame loop, and paints. Its
 * props never change, so React stops at the memo boundary and the page's own
 * updates cost the chart nothing. The loop draws at most every FRAME_MS, and
 * only when the data version moved or a tile is still pulsing; once a scan
 * settles it costs one early return per frame.
 *
 * The geometry, colours and pulse are ported from the SVG so the chart looks
 * the same as it did.
 */

/* the strip at the foot of the block, and the coordinate space its 100 bars
   were drawn in */
const BAR_HEIGHT = 24;
const BAR_VB = { width: 320, height: 20 };

/** the draw ceiling: the pulse reads as motion well below screen rate */
const FRAME_MS = 80;

const FAIL = "214,57,100";
const PASS = "71,148,72";
const PENDING = "rgba(128,128,128,0.921)";
const EMPTY = "rgba(128,128,128,0.042)";
/* the kitt bar's two ends, as the gradients named them */
const BAR_DONE = "73,99,187";
const BAR_LEFT = "126,27,8";

/* fixed tables rather than per-draw randomness, so a tile keeps its shade and
   its pulse period from one frame to the next */
const alphas = [...Array(500)].map(() => +(Math.random() * 0.5 + 0.25).toFixed(3));
const tileDur = [...Array(500)].map(() => Math.random() * 2.01 + 1.03);
const barDur = [...Array(100)].map(() => Math.random() * 2.01 + 0.03);

/* the space between tiles, and the smallest tile worth drawing */
const GAP = 2;
const MIN_TILE = 3;
/* the proportion a host name sits in comfortably; the packing is scored
   against it rather than against a square */
const IDEAL_RATIO = 4;

/* What a host still being scanned is worth on screen: the ones in flight are
   the ones being read, so the block reserves room for them at about this size
   and packs everything that has already answered into what is left. */
const RUN_W = 60;
const RUN_H = 20;
/* how much of the block the running band may take while anything has settled */
const RUN_SHARE = 0.6;

/**
 * The block, split between what is running and what has settled.
 *
 * With one grid for all of them, a /24 drew 256 tiles the size of a word and
 * the handful actually in flight were lost among them. The running hosts get a
 * band of their own, sized for RUN_W x RUN_H tiles - enough to carry a name -
 * and it gives way as they finish: it never takes more than RUN_SHARE of the
 * block while anything has settled, and it is the whole block before anything
 * has.
 */
export function bandsFor(width, height, runningCount, settledCount) {
  if (!runningCount) {
    return { run: null, settled: { y: 0, height, count: settledCount } };
  }

  const cols = Math.max(1, Math.floor((width + GAP) / (RUN_W + GAP)));
  const wanted = Math.ceil(runningCount / cols) * (RUN_H + GAP);

  if (!settledCount) {
    return { run: { y: 0, height, count: runningCount }, settled: null };
  }

  const runHeight = Math.max(RUN_H, Math.min(wanted, height * RUN_SHARE));
  const rest = Math.max(0, height - runHeight - GAP * 3);
  return {
    run: { y: 0, height: runHeight, count: runningCount },
    settled: { y: runHeight + GAP * 3, height: rest, count: settledCount },
  };
}

/**
 * The grid, packed to the hosts that are actually known.
 *
 * The SVG derived its grid from a formula with a fixed 14x8 seed and hard caps,
 * which left most of the block empty for a small scan and clipped a large one.
 * This walks the column counts the box can hold, takes rows = ceil(n / cols) so
 * the only gap is a partial last row, and keeps the shape that gives the
 * largest readable tile. Because it is computed from the live count on every
 * draw, the grid re-tiles itself as hosts arrive.
 */
export function tileLayout(boxWidth, boxHeight, count) {
  const n = Math.max(1, count);
  const maxCols = Math.max(1, Math.floor((boxWidth + GAP) / (MIN_TILE + GAP)));

  let best = null;
  for (let cols = 1; cols <= Math.min(n, maxCols); cols++) {
    const rows = Math.ceil(n / cols);
    const width = (boxWidth - (cols - 1) * GAP) / cols;
    const height = (boxHeight - (rows - 1) * GAP) / rows;
    if (height < MIN_TILE) continue;

    /* Three things decide it: how big the tile is, how close it sits to the
       shape a hostname reads well in, and how much of the grid is actually
       used - so a layout that fills the block beats one that leaves a ragged
       row behind. */
    const shape = 1 / (1 + Math.abs(Math.log((width / height) / IDEAL_RATIO)));
    const fill = n / (cols * rows);
    const score = width * height * shape * fill;

    if (!best || score > best.score) best = { cols, rows, width, height, score };
  }

  /* more hosts than the block can hold at a readable size: fill it at the
     floor and let the rest wait for the log below */
  if (!best) {
    const cols = maxCols;
    const rows = Math.max(1, Math.floor((boxHeight + GAP) / (MIN_TILE + GAP)));
    best = {
      cols,
      rows,
      width: (boxWidth - (cols - 1) * GAP) / cols,
      height: (boxHeight - (rows - 1) * GAP) / rows,
    };
  }

  return {
    cols: best.cols,
    rows: best.rows,
    width: best.width,
    height: best.height,
    gap: GAP,
    capacity: best.cols * best.rows,
    /* the label only goes on when the tile can hold one */
    showText: best.width >= 42 && best.height >= 11,
    fontSize: Math.min(12, Math.max(7, best.height * 0.52)),
  };
}

/** The host name, trimmed to what the tile can show. */
function fitLabel(ctx, text, room) {
  if (ctx.measureText(text).width <= room) return text;
  let cut = text.length - 1;
  while (cut > 1 && ctx.measureText(`${text.slice(0, cut)}...`).width > room) cut--;
  return `${text.slice(0, cut)}...`;
}

/** the <animate> the pending tiles carried: 0.01 up to 1 over the first 40%. */
function pulse(now, dur) {
  const phase = (now / 1000 / dur) % 1;
  return phase < 0.4
    ? 0.01 + 0.99 * (phase / 0.4)
    : 1 - 0.99 * ((phase - 0.4) / 0.6);
}

function roundRect(ctx, x, y, w, h, r) {
  const rad = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rad, y);
  ctx.arcTo(x + w, y, x + w, y + h, rad);
  ctx.arcTo(x + w, y + h, x, y + h, rad);
  ctx.arcTo(x, y + h, x, y, rad);
  ctx.arcTo(x, y, x + w, y, rad);
  ctx.closePath();
  ctx.fill();
}

/** host, state and message for one slot - the SVG's per-rect lookup. */
export function tileAt(source, id) {
  const { tree, keys, done } = source;
  const key = keys[id];
  if (key === undefined) return null;
  const first = tree[key]?.[0];
  if (!first) return null;

  const host = `${first.host || first.data_source_id || first.protocol}`;
  const message = first.message_type === "success"
    ? "Scan Completed Successfully"
    : `${first.message || ""}`;
  return { host, message, state: done[id] };
}

/** One band of tiles: `ids` are slots in the source's own order. */
function drawBand(ctx, source, now, band, ids, boxWidth) {
  const { width, height, cols, gap, capacity, showText, fontSize } =
    tileLayout(boxWidth, band.height, ids.length);

  if (showText) ctx.font = `${fontSize.toFixed(1)}px Inter, sans-serif`;
  const radius = Math.min(3, width / 6, height / 6);

  for (let seat = 0; seat < capacity; seat++) {
    const x = (seat % cols) * (width + gap);
    const y = band.y + Math.floor(seat / cols) * (height + gap);

    /* only the tail of the last row, now that the grid is cut to the count */
    if (seat >= ids.length) {
      ctx.fillStyle = EMPTY;
      ctx.fillRect(x, y, width, height);
      continue;
    }

    const slot = ids[seat];
    const tile = tileAt(source, slot);
    const alpha = alphas[slot % 500];
    const state = tile ? tile.state : undefined;

    ctx.globalAlpha = state === undefined ? pulse(now, tileDur[slot % 500]) : 1;
    ctx.fillStyle =
      state === false ? `rgba(${FAIL},${alpha})` :
      state === true ? `rgba(${PASS},${alpha})` : PENDING;
    roundRect(ctx, x, y, width, height, radius);
    ctx.globalAlpha = 1;

    if (showText && tile) {
      ctx.fillStyle = "white";
      ctx.fillText(fitLabel(ctx, tile.host, width - 8), x + width / 2, y + height / 2);
    }
  }

  /* what the hit test needs: where the band starts, how big a tile is, and
     which slot each seat holds */
  return { top: band.y, tileW: width, tileH: height, cols, gap, ids,
           rows: Math.ceil(capacity / cols) };
}

function drawTiles(ctx, source, now, boxWidth, boxHeight) {
  const { keys, done } = source;

  /* in flight, and answered: a slot with no outcome yet is still being read */
  const running = [];
  const settled = [];
  for (let i = 0; i < keys.length; i++) {
    (done[i] === undefined ? running : settled).push(i);
  }

  const bands = bandsFor(boxWidth, boxHeight, running.length, settled.length);

  ctx.clearRect(0, 0, boxWidth, boxHeight);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  const painted = [];
  if (bands.run) painted.push(drawBand(ctx, source, now, bands.run, running, boxWidth));
  if (bands.settled) painted.push(drawBand(ctx, source, now, bands.settled, settled, boxWidth));
  return painted;
}

function drawBar(ctx, source, now, boxWidth, boxHeight) {
  const { percentage } = source;
  ctx.clearRect(0, 0, boxWidth, boxHeight);
  ctx.save();

  /* the bars were drawn in a 320x20 space; it is stretched across whatever
     width the panel gives, rather than scaled uniformly as the SVG did in its
     fixed 370-wide box. Its one <text> sat at y=-5, outside the viewBox and
     clipped away, so it is not drawn here either. */
  ctx.scale(boxWidth / BAR_VB.width, boxHeight / BAR_VB.height);

  const fade = (rgb, end) => {
    const g = ctx.createLinearGradient(0, 0, 300, 0);
    g.addColorStop(0, `rgba(${rgb},${end})`);
    g.addColorStop(0.4, `rgb(${rgb})`);
    g.addColorStop(0.6, `rgb(${rgb})`);
    g.addColorStop(1, `rgba(${rgb},${end})`);
    return g;
  };
  const doneFill = fade(BAR_DONE, 0.082);
  const leftFill = fade(BAR_LEFT, 0.842);

  for (let i = 0; i < 100; i++) {
    const ahead = percentage <= i;
    ctx.globalAlpha = ahead ? pulse(now, barDur[i]) : 1;
    ctx.fillStyle = percentage < i ? leftFill : doneFill;
    /* pending bars breathe between 1 and 15 tall, as the height <animate> did */
    const phase = (now / 1000 / barDur[i]) % 1;
    const h = ahead ? (phase < 0.4 ? 1 + 7 * (phase / 0.4) : 8 + 7 * ((phase - 0.4) / 0.6)) : 10;
    ctx.fillRect(3.2 * i, BAR_VB.height - h, 3, h);
  }

  ctx.globalAlpha = 1;
  ctx.restore();
}

function fit(canvas, width, height) {
  const dpr = window.devicePixelRatio || 1;
  const w = Math.round(width * dpr);
  const h = Math.round(height * dpr);
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext("2d");
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  return ctx;
}

function ScanProgressTiles({ source, interactive = false }) {
  const wrapRef = useRef(null);
  const tilesRef = useRef(null);
  const barRef = useRef(null);
  const barWrapRef = useRef(null);
  /* the bands as last painted, so a click lands on the tile under the pointer */
  const bandsRef = useRef(null);
  /* the tooltip is written to directly: it follows the pointer between frames
     and must not put React through a render to do it */
  const tipRef = useRef(null);
  const tipShownFor = useRef(null);
  /* the box the chart fills, kept in a ref: a resize must reach the draw loop,
     not React */
  const sizeRef = useRef({ w: 0, h: 0 });

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;

    const read = () => {
      sizeRef.current = { w: Math.round(el.clientWidth), h: Math.round(el.clientHeight) };
    };
    read();

    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let raf = 0;
    let lastPaint = 0;
    let lastVersion = -1;
    let lastDpr = 0;
    let lastSize = "";
    let lastBar = null;

    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      if (now - lastPaint < FRAME_MS) return;

      const s = source.current;
      const { w, h } = sizeRef.current;
      if (!s || w <= 0 || h <= 0) return;

      /* the bar's visibility was a style on a re-rendered element; it is set
         here instead so the whole chart can stay outside React's updates */
      if (barWrapRef.current && s.showBar !== lastBar) {
        lastBar = s.showBar;
        barWrapRef.current.style.display = s.showBar ? "block" : "none";
      }

      const dpr = window.devicePixelRatio || 1;
      const size = `${w}x${h}`;
      const live = s.keys.length > s.doneCount || (s.showBar && s.percentage < 100);
      const stale = s.version !== lastVersion || dpr !== lastDpr || size !== lastSize;
      if (!stale && !live) return;

      lastPaint = now;
      lastVersion = s.version;
      lastDpr = dpr;
      lastSize = size;

      const gridHeight = Math.max(0, h - (s.showBar ? BAR_HEIGHT : 0));
      if (tilesRef.current && gridHeight > 0) {
        bandsRef.current = drawTiles(fit(tilesRef.current, w, gridHeight), s, now, w, gridHeight);
      }
      if (s.showBar && barRef.current) {
        drawBar(fit(barRef.current, w, BAR_HEIGHT), s, now, w, BAR_HEIGHT);
      }
    };

    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [source]);

  /* the SVG gave every rect a <title> and a click; kept for the in-card chart,
     where the tiles are not covered by the log and can still be used */
  const locate = (event) => {
    const s = source.current;
    const bands = bandsRef.current;
    if (!s || !bands) return null;

    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    for (const band of bands) {
      const local = y - band.top;
      if (local < 0) continue;
      const row = Math.floor(local / (band.tileH + band.gap));
      const col = Math.floor(x / (band.tileW + band.gap));
      if (row < 0 || row >= band.rows || col < 0 || col >= band.cols) continue;
      const slot = band.ids[row * band.cols + col];
      if (slot === undefined) continue;
      return tileAt(s, slot);
    }
    return null;
  };

  /* What the tile under the pointer is: its host, what it last said, and the
     state its colour is standing for. The old SVG carried this as a <title> on
     every rect; one element that follows the pointer costs nothing and can be
     dressed as the dashboard dresses everything else. */
  const showTip = (event) => {
    const tip = tipRef.current;
    if (!tip) return;

    const tile = locate(event);
    if (!tile) {
      if (tipShownFor.current !== null) {
        tipShownFor.current = null;
        tip.style.opacity = "0";
        tip.style.visibility = "hidden";
      }
      return;
    }

    if (tipShownFor.current !== tile.host) {
      tipShownFor.current = tile.host;
      tip.querySelector(".scan-tip__host").textContent = tile.host;
      tip.querySelector(".scan-tip__msg").textContent =
        tile.message || (tile.state === undefined ? "Scanning..." : "");
      tip.dataset.state =
        tile.state === true ? "pass" : tile.state === false ? "fail" : "run";
      tip.style.visibility = "visible";
      tip.style.opacity = "1";
    }

    /* it rides the pointer: to its right and below by default, to the other
       side when that would leave the block, and held inside it either way */
    const box = wrapRef.current.getBoundingClientRect();
    const x = event.clientX - box.left;
    const y = event.clientY - box.top;
    const tw = tip.offsetWidth;
    const th = tip.offsetHeight;

    let tx = x + 14;
    if (tx + tw > box.width) tx = x - tw - 14;
    tx = Math.min(Math.max(0, tx), Math.max(0, box.width - tw));

    let ty = y + 16;
    if (ty + th > box.height) ty = y - th - 12;
    ty = Math.min(Math.max(0, ty), Math.max(0, box.height - th));

    tip.style.transform = `translate(${Math.round(tx)}px, ${Math.round(ty)}px)`;
  };

  const hideTip = () => {
    const tip = tipRef.current;
    tipShownFor.current = null;
    if (tip) {
      tip.style.opacity = "0";
      tip.style.visibility = "hidden";
    }
  };

  const handlers = interactive ? {
    onMouseMove: showTip,
    /* both: leave is the one that matters, out is the one that still fires when
       the pointer jumps straight off the canvas */
    onMouseLeave: hideTip,
    onMouseOut: hideTip,
    onClick: (event) => {
      const tile = locate(event);
      if (tile) source.current?.onPick?.(tile.host);
    },
  } : {};

  /* Fills whatever box the caller gives it - the log panel hands it the full
     width and a share of the height - rather than the 370x480 the SVG was
     nailed to. */
  return (
    <div ref={wrapRef} style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      <canvas
        ref={tilesRef}
        {...handlers}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: `calc(100% - ${BAR_HEIGHT}px)`,
          display: "block",
          cursor: interactive ? "pointer" : "default",
        }}
      />
      {interactive &&
        <div ref={tipRef} className="scan-tip" aria-hidden="true">
          <span className="scan-tip__dot" />
          <span className="scan-tip__text">
            <b className="scan-tip__host" />
            <span className="scan-tip__msg" />
          </span>
        </div>}

      <div
        ref={barWrapRef}
        className="scanniningProgressBar"
        style={{
          /* hidden until the loop says otherwise, so a mount before the first
             scan cannot flash the bar */
          display: "none",
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          height: BAR_HEIGHT,
          overflow: "hidden",
        }}
      >
        <canvas ref={barRef} style={{ width: "100%", height: BAR_HEIGHT, display: "block" }} />
      </div>
    </div>
  );
}

export default memo(ScanProgressTiles);
